import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/responsive-modal";
import { YawGauge } from "@/components/yaw-gauge";
import { computeSide, type SideMeasurements } from "@/domain/yaw-check";
import { useAuth } from "@/providers/auth-provider";
import { toast } from "sonner";
import type { components } from "@/api/generated/schema";
import type { BuildId } from "@/types/identifiers";

type YawCheckCreate = components["schemas"]["YawCheckCreate"];
type YawCheckUpdate = components["schemas"]["YawCheckUpdate"];
type YawCheckResponse = components["schemas"]["YawCheckResponse"];

interface YawCheckFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Resolves on mutation success; rejects on failure so the drawer can stay open.
   * Receives a full create payload in create mode, or a delta of changed fields in
   * edit mode (Layer 1 of stale-write mitigation — never overwrites unchanged fields).
   */
  onSubmit: (data: YawCheckCreate | YawCheckUpdate) => Promise<void>;
  isSubmitting: boolean;
  buildId: BuildId;
  /** When set, form opens in edit mode with pre-populated values. */
  initialData?: YawCheckResponse;
  /** Newest check id at drawer open — drafts saved against an older value are discarded. */
  latestCheckId: string | null;
}

const INITIAL_SIDE: SideMeasurements = {
  fwd_static: 0, fwd_max: 0, fwd_min: 0,
  aft_static: 0, aft_max: 0, aft_min: 0,
};

type MeasurementKey = keyof SideMeasurements;
const ROWS: { label: string; fwd: MeasurementKey; aft: MeasurementKey }[] = [
  { label: "static", fwd: "fwd_static", aft: "aft_static" },
  { label: "max", fwd: "fwd_max", aft: "aft_max" },
  { label: "min", fwd: "fwd_min", aft: "aft_min" },
];

const DRAFT_TTL_MS = 8 * 60 * 60 * 1000; // 8h — single workday recovery window

interface YawDraft {
  prt: SideMeasurements;
  stb: SideMeasurements;
  savedAt: number;
  latestCheckId: string | null;
}

function draftKey(buildId: BuildId, userSub: string) {
  return `yaw-draft-${buildId}-${userSub}`;
}

function extractSideFromResponse(check: YawCheckResponse, side: "prt" | "stb"): SideMeasurements {
  return {
    fwd_static: check[`${side}_fwd_static`],
    fwd_max: check[`${side}_fwd_max`],
    fwd_min: check[`${side}_fwd_min`],
    aft_static: check[`${side}_aft_static`],
    aft_max: check[`${side}_aft_max`],
    aft_min: check[`${side}_aft_min`],
  };
}

function sidesEqual(a: SideMeasurements, b: SideMeasurements): boolean {
  return (Object.keys(a) as MeasurementKey[]).every((k) => a[k] === b[k]);
}

function loadInitialState(
  buildId: BuildId,
  userSub: string,
  latestCheckId: string | null,
  initialData?: YawCheckResponse,
) {
  if (initialData) {
    return {
      prt: extractSideFromResponse(initialData, "prt"),
      stb: extractSideFromResponse(initialData, "stb"),
      draftRestored: false,
    };
  }

  const key = draftKey(buildId, userSub);
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      const draft = JSON.parse(stored) as Partial<YawDraft>;
      const savedAt = draft.savedAt ?? 0;
      const tooOld = Date.now() - savedAt > DRAFT_TTL_MS;
      const supersededByNewerSave = (draft.latestCheckId ?? null) !== latestCheckId;
      if (tooOld || supersededByNewerSave) {
        localStorage.removeItem(key);
      } else if (draft.prt && draft.stb) {
        return {
          prt: draft.prt,
          stb: draft.stb,
          draftRestored: true,
        };
      }
    } catch {
      localStorage.removeItem(key);
    }
  }

  return {
    prt: { ...INITIAL_SIDE },
    stb: { ...INITIAL_SIDE },
    draftRestored: false,
  };
}

const TAP_STEP = 0.25;
const DOUBLE_TAP_STEP = 1.0;
const HOLD_DELAY = 400;
const REPEAT_INTERVAL = 100;
const DOUBLE_TAP_MS = 250;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function StepperInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const [fineDir, setFineDir] = useState<-1 | 0 | 1>(0);
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; });
  const doubleTapRef = useRef<{ dir: -1 | 1; timer: ReturnType<typeof setTimeout> } | null>(null);

  const startHold = useCallback((dir: -1 | 1) => {
    let held = false;
    let interval: ReturnType<typeof setInterval> | undefined;

    // Long press → fine mode with auto-repeat
    const timeout = setTimeout(() => {
      held = true;
      setFineDir(dir);
      onChange(round2(valueRef.current + dir * TAP_STEP));
      interval = setInterval(() => {
        onChange(round2(valueRef.current + dir * TAP_STEP));
      }, REPEAT_INTERVAL);
    }, HOLD_DELAY);

    const cleanup = () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
      setFineDir(0);

      if (held) return; // long-press already handled

      // Tap: check for double-tap on same direction
      const prev = doubleTapRef.current;
      if (prev && prev.dir === dir) {
        // Double tap — first tap already applied ±0.25, add ±0.75 more for ±1.0 total
        clearTimeout(prev.timer);
        doubleTapRef.current = null;
        onChange(round2(valueRef.current + dir * (DOUBLE_TAP_STEP - TAP_STEP)));
      } else {
        // First tap — apply ±0.25 immediately
        if (prev) clearTimeout(prev.timer);
        onChange(round2(valueRef.current + dir * TAP_STEP));
        const timer = setTimeout(() => { doubleTapRef.current = null; }, DOUBLE_TAP_MS);
        doubleTapRef.current = { dir, timer };
      }
    };

    document.addEventListener("pointerup", cleanup, { once: true });
    document.addEventListener("pointercancel", cleanup, { once: true });
  }, [onChange]);

  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        onPointerDown={(e) => { e.preventDefault(); startHold(-1); }}
        className={`flex-shrink-0 w-[38px] h-[38px] rounded-[9px] border-[1.5px] flex items-center justify-center text-lg select-none touch-manipulation transition-all active:scale-95 ${
          fineDir === -1
            ? "border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-950"
            : "border-border bg-card text-foreground active:bg-muted"
        }`}
      >
        &#8722;
      </button>

      <div className="flex-1 min-w-0 text-center">
        <div className="w-full text-xl font-semibold tabular-nums tracking-tight py-1.5 leading-none select-none">
          {value.toFixed(2)}
        </div>
        <div className={`text-[8px] leading-none h-2.5 ${
          fineDir !== 0 ? "text-blue-500 font-medium" : "text-muted-foreground/0"
        }`}>
          {fineDir !== 0 ? `±${TAP_STEP}` : "\u00A0"}
        </div>
      </div>

      <button
        type="button"
        onPointerDown={(e) => { e.preventDefault(); startHold(1); }}
        className={`flex-shrink-0 w-[38px] h-[38px] rounded-[9px] border-[1.5px] flex items-center justify-center text-lg select-none touch-manipulation transition-all active:scale-95 ${
          fineDir === 1
            ? "border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-950"
            : "border-border bg-card text-foreground active:bg-muted"
        }`}
      >
        +
      </button>
    </div>
  );
}

function SideForm({
  side,
  onChange,
  activeSide,
}: {
  side: SideMeasurements;
  onChange: (updated: SideMeasurements) => void;
  activeSide: "prt" | "stb";
}) {
  const computed = computeSide(side);
  const borderColor = activeSide === "prt" ? "border-l-red-400" : "border-l-emerald-400";

  const handleChange = (key: MeasurementKey, value: number) => {
    if (key === "fwd_static" || key === "aft_static") {
      const updated = { ...side, [key]: value };
      onChange({
        ...updated,
        fwd_max: updated.fwd_static, fwd_min: updated.fwd_static,
        aft_max: updated.aft_static, aft_min: updated.aft_static,
      });
    } else {
      onChange({ ...side, [key]: value });
    }
  };

  return (
    <div className="space-y-2">
      {/* Row groups: Static / Max / Min */}
      {ROWS.map((row) => {
        const totKey = `tot_${row.label}` as "tot_static" | "tot_max" | "tot_min";
        const totVal = computed[totKey];
        return (
          <div key={row.label} className={`border-l-3 rounded-r-lg bg-card p-2.5 ${borderColor}`}>
            <div className="flex items-center mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{row.label}</span>
              <span className="ml-auto text-sm font-semibold tabular-nums">
                <span className="text-[10px] font-normal text-muted-foreground mr-1">tot</span>
                {totVal.toFixed(2)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wider text-center mb-0.5">fwd</div>
                <StepperInput value={side[row.fwd]} onChange={(v) => handleChange(row.fwd, v)} />
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wider text-center mb-0.5">aft</div>
                <StepperInput value={side[row.aft]} onChange={(v) => handleChange(row.aft, v)} />
              </div>
            </div>
          </div>
        );
      })}

      {/* YAW angles */}
      <div className="flex items-center rounded-lg border border-border bg-card px-3 py-2.5">
        <div className="flex-1 text-center">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Min</div>
          <div className="text-base font-semibold tabular-nums tracking-tight">{computed.tot_min_yaw.toFixed(3)}<span className="text-[11px] font-normal text-muted-foreground">°</span></div>
        </div>
        <div className="flex-1 text-center">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Static</div>
          <div className="text-base font-semibold tabular-nums tracking-tight">{computed.tot_yaw.toFixed(3)}<span className="text-[11px] font-normal text-muted-foreground">°</span></div>
        </div>
        <div className="flex-1 text-center">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Max</div>
          <div className="text-base font-semibold tabular-nums tracking-tight">{computed.tot_max_yaw.toFixed(3)}<span className="text-[11px] font-normal text-muted-foreground">°</span></div>
        </div>
        <div className="pl-2.5 border-l border-border text-right">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Range</div>
          <div className="text-sm font-medium tabular-nums text-muted-foreground">{computed.range.toFixed(3)}°</div>
        </div>
      </div>

      {/* Gauge */}
      <div className="rounded-lg border border-border bg-card px-3 py-2.5">
        <YawGauge staticYaw={computed.tot_yaw} minYaw={computed.tot_min_yaw} maxYaw={computed.tot_max_yaw} />
      </div>
    </div>
  );
}

type ActiveTab = "prt" | "stb";

/**
 * Inner form component — remounted via key each time the drawer opens.
 * This avoids effect-based state synchronization: useState initializers
 * run fresh on mount with the correct initial values.
 */
function YawCheckFormContent({ onOpenChange, onSubmit, isSubmitting, buildId, initialData, latestCheckId }: Omit<YawCheckFormDrawerProps, "open">) {
  const isEditMode = !!initialData;
  const { user } = useAuth();
  const userSub = user.sub;
  const init = loadInitialState(buildId, userSub, latestCheckId, initialData);

  const [prt, setPrt] = useState<SideMeasurements>(init.prt);
  const [stb, setStb] = useState<SideMeasurements>(init.stb);
  const [activeTab, setActiveTab] = useState<ActiveTab>("prt");

  // Show draft-restored toast once on mount
  const didToastRef = useRef(false);
  useEffect(() => {
    if (init.draftRestored && !didToastRef.current) {
      didToastRef.current = true;
      toast.info("Draft restored");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dirty tracking
  const isDirty = useMemo(() => {
    if (isEditMode && initialData) {
      const origPrt = extractSideFromResponse(initialData, "prt");
      const origStb = extractSideFromResponse(initialData, "stb");
      return !sidesEqual(prt, origPrt) || !sidesEqual(stb, origStb);
    }
    return !sidesEqual(prt, INITIAL_SIDE) || !sidesEqual(stb, INITIAL_SIDE);
  }, [prt, stb, isEditMode, initialData]);

  // Draft auto-save (create mode only, debounced).
  // When the form returns to a clean state (e.g. user zeros all values),
  // we proactively clear any saved draft so it can't be resurrected next session.
  const draftTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (isEditMode) return;
    clearTimeout(draftTimerRef.current);
    if (!isDirty) {
      localStorage.removeItem(draftKey(buildId, userSub));
      return;
    }
    draftTimerRef.current = setTimeout(() => {
      const draft: YawDraft = { prt, stb, savedAt: Date.now(), latestCheckId };
      localStorage.setItem(draftKey(buildId, userSub), JSON.stringify(draft));
    }, 500);
    return () => clearTimeout(draftTimerRef.current);
  }, [isEditMode, isDirty, prt, stb, buildId, userSub, latestCheckId]);

  const handleSubmit = useCallback(async () => {
    let payload: YawCheckCreate | YawCheckUpdate;
    if (isEditMode && initialData) {
      // Edit mode: send only fields that the user actually changed. Limits
      // blast radius when two users edit the same unsigned check concurrently.
      const update: YawCheckUpdate = {};
      const sides = [
        ["prt", prt, extractSideFromResponse(initialData, "prt")] as const,
        ["stb", stb, extractSideFromResponse(initialData, "stb")] as const,
      ];
      for (const [side, cur, orig] of sides) {
        for (const k of Object.keys(cur) as MeasurementKey[]) {
          if (cur[k] !== orig[k]) {
            update[`${side}_${k}` as const] = cur[k];
          }
        }
      }
      if (Object.keys(update).length === 0) {
        // Nothing changed — skip the API round-trip and the audit-log noise.
        onOpenChange(false);
        return;
      }
      payload = update;
    } else {
      payload = {
        prt_fwd_static: prt.fwd_static,
        prt_fwd_max: prt.fwd_max,
        prt_fwd_min: prt.fwd_min,
        prt_aft_static: prt.aft_static,
        prt_aft_max: prt.aft_max,
        prt_aft_min: prt.aft_min,
        stb_fwd_static: stb.fwd_static,
        stb_fwd_max: stb.fwd_max,
        stb_fwd_min: stb.fwd_min,
        stb_aft_static: stb.aft_static,
        stb_aft_max: stb.aft_max,
        stb_aft_min: stb.aft_min,
      };
    }
    try {
      await onSubmit(payload);
      localStorage.removeItem(draftKey(buildId, userSub));
      onOpenChange(false);
    } catch {
      // Mutation failed — keep drawer open with values intact, draft preserved
      // for retry. The action hook already toasted the error.
    }
  }, [prt, stb, isEditMode, initialData, onSubmit, buildId, userSub, onOpenChange]);

  const handleInterceptClose = useCallback(() => {
    if (isDirty) {
      if (window.confirm("You have unsaved changes. Discard?")) {
        if (!isEditMode) localStorage.removeItem(draftKey(buildId, userSub));
        onOpenChange(false);
      }
    } else {
      onOpenChange(false);
    }
  }, [isDirty, isEditMode, buildId, userSub, onOpenChange]);

  const title = isEditMode ? "Edit YAW Check" : "Record YAW Check";
  const buttonLabel = isEditMode
    ? (isSubmitting ? "Updating..." : "Update YAW Check")
    : (isSubmitting ? "Saving..." : "Save YAW Check");

  const footer = (
    <Button
      className="w-full"
      onClick={handleSubmit}
      disabled={isSubmitting}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <ResponsiveModal
      open={true}
      onOpenChange={onOpenChange}
      title={title}
      footer={footer}
      dismissible={!isDirty}
      onInterceptClose={isDirty ? handleInterceptClose : undefined}
    >
      <div className="space-y-3">
        {/* PRT/STB tabs */}
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {(["prt", "stb"] as const).map((tab) => {
            const active = activeTab === tab;
            const color = tab === "prt"
              ? active ? "ring-2 ring-red-400 text-red-600 dark:ring-red-500 dark:text-red-400" : "text-muted-foreground hover:text-foreground"
              : active ? "ring-2 ring-emerald-400 text-emerald-600 dark:ring-emerald-500 dark:text-emerald-400" : "text-muted-foreground hover:text-foreground";
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 h-[34px] rounded-md text-xs font-medium transition-colors ${color}`}
              >
                {tab === "prt" ? "PORT" : "STBD"}
              </button>
            );
          })}
        </div>

        {/* Active side form */}
        {activeTab === "prt" ? (
          <SideForm side={prt} onChange={setPrt} activeSide="prt" />
        ) : (
          <SideForm side={stb} onChange={setStb} activeSide="stb" />
        )}
      </div>
    </ResponsiveModal>
  );
}

export function YawCheckFormDrawer({ open, ...rest }: YawCheckFormDrawerProps) {
  if (!open) return null;

  return <YawCheckFormContent {...rest} />;
}
